import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  EventBusy as CancelIcon,
  Security as PrivacyIcon,
  Gavel as TermsIcon,
  CheckCircleOutline as CheckIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';

function PoliciesPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to="/"
          startIcon={<BackIcon />}
          sx={{ mb: 2 }}
        >
          Back to Booking
        </Button>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Policies
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please read our policies before booking an appointment.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            minWidth: { xs: 'auto', sm: 140 },
            px: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Tab icon={<CancelIcon />} label="Cancellation" iconPosition="start" />
        <Tab icon={<PrivacyIcon />} label="Privacy" iconPosition="start" />
        <Tab icon={<TermsIcon />} label="Terms" iconPosition="start" />
      </Tabs>

      {/* Cancellation Policy */}
      {tab === 0 && (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Cancellation & No-Show Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We understand plans change. Please give us as much notice as possible if you need to cancel or reschedule.
            </Typography>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
              Cancellation Fees
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="More than 48 hours notice"
                  secondary="No charge"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon sx={{ color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="24-48 hours notice"
                  secondary="50% of service price"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Less than 24 hours notice or no-show"
                  secondary="100% of service price"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              How to Cancel
            </Typography>
            <Typography variant="body1" paragraph>
              Log in to your Customer Portal to request a cancellation or reschedule your appointment.
            </Typography>
            <Button
              component={Link}
              to="/portal"
              variant="outlined"
              size="small"
            >
              Go to Customer Portal
            </Button>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Late Arrivals
            </Typography>
            <Typography variant="body1" paragraph>
              If you arrive late, your appointment may be shortened to avoid impacting other clients. The full service price will still apply.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              No-Show Protection
            </Typography>
            <Typography variant="body1">
              We require card details when booking. Your card is NOT charged at the time of booking. It is only used if you miss an appointment without adequate notice, as outlined above.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Privacy Policy */}
      {tab === 1 && (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              Studio Jen ("we", "us") is committed to protecting your personal data.
            </Typography>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              What We Collect
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Name, email, and phone number (for bookings and communication)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Booking history and preferences" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Payment card details (securely stored by Stripe - we never see your full card number)" />
              </ListItem>
            </List>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              How We Use Your Data
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="To manage your bookings and send appointment reminders" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="To process payments and no-show charges if applicable" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="To contact you about your appointments" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your data for 2 years after your last booking. You can request deletion at any time via the Customer Portal.
            </Typography>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              Data Sharing
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We use Stripe for secure payment processing" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We use Brevo for appointment reminder emails" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We do not sell your data to third parties" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Your Rights
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Access your data via the Customer Portal" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Request correction or deletion of your data" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Withdraw consent for marketing communications" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary">
              For data-related requests, please use the Customer Portal or contact us directly.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Last updated: February 2026
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Terms & Conditions */}
      {tab === 2 && (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Terms & Conditions
            </Typography>
            <Typography variant="body1" paragraph>
              By booking an appointment with Studio Jen, you agree to the following:
            </Typography>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              1. Bookings
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Bookings are confirmed once you receive a confirmation email/notification" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We require valid card details to secure your booking" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Your card is not charged at booking - only in case of late cancellation or no-show" />
              </ListItem>
            </List>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              2. Cancellation Policy
            </Typography>
            <Typography variant="body1" paragraph>
              See our Cancellation Policy tab for fees and notice requirements. Cancellation requests must be made via the Customer Portal.
            </Typography>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              3. Pricing
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="All prices are in GBP and include VAT where applicable" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Prices are subject to change but your booking will be honoured at the quoted price" />
              </ListItem>
            </List>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              4. Liability
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We carry appropriate liability insurance" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Please inform us of any allergies or skin conditions before your treatment" />
              </ListItem>
            </List>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              5. Gift Vouchers & Promotions
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Discount codes cannot be combined unless stated" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Gift vouchers are valid for 12 months from purchase" />
              </ListItem>
            </List>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }} gutterBottom>
              6. Photography
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We may occasionally take photos for portfolio/social media" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="We will always ask your permission first" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="caption" color="text.secondary">
              Last updated: February 2026
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          component={Link}
          to="/"
          variant="contained"
          size="large"
        >
          Book an Appointment
        </Button>
      </Box>
    </Box>
  );
}

export default PoliciesPage;
